import { Button, Card, Empty, Input, Select, Space, Table, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type BatchRow = {
  id: string;
  status: string;
  school_decision_reason?: string;
  source_session_name?: string | null;
  target_session_name?: string | null;
  source_session_code?: string | null;
  target_session_code?: string | null;
  item_count?: number;
  created_at?: string | null;
};

const STATUSES = ['draft', 'pending_approval', 'approved', 'executed'] as const;

function statusColor(status?: string): string | undefined {
  if (status === 'executed') return 'green';
  if (status === 'approved') return 'blue';
  if (status === 'pending_approval') return 'gold';
  return 'default';
}

function sessionLabel(code?: string | null, name?: string | null): string {
  return [code, name].filter(Boolean).join(' · ') || '—';
}

export default function ProgressionListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const canManage = Boolean(
    useAuthStore((state) => state.user?.permissions?.includes('education.progression.manage'))
  );
  const status = params.get('status') || undefined;
  const sourceSessionId = params.get('source_session_id') || undefined;
  const targetSessionId = params.get('target_session_id') || undefined;
  const q = params.get('q') || undefined;

  const query = useQuery({
    queryKey: ['education-progression-batches', status, sourceSessionId, targetSessionId, q],
    queryFn: () =>
      api.get<BatchRow[]>('/education/progression-batches', {
        params: {
          status,
          source_session_id: sourceSessionId,
          target_session_id: targetSessionId,
          q,
        },
      }),
  });
  const rows = Array.isArray(query.data?.data) ? query.data!.data : [];

  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  return (
    <EducationPageChrome
      title={t('progressionTitle')}
      description={t('progressionIntro')}
      tags={
        <Tag>
          {rows.length} {t('progressionBatches', { defaultValue: 'batches' })}
        </Tag>
      }
      extra={
        canManage ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/education/progression/new')}>
            {t('newProgressionBatch', { defaultValue: 'New progression batch' })}
          </Button>
        ) : null
      }
    >
      <Card size="small">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <Input.Search
              allowClear
              placeholder={t('search')}
              defaultValue={q}
              onSearch={(value) => setFilter('q', value || undefined)}
              style={{ width: 240 }}
            />
            <Select
              allowClear
              placeholder={t('status')}
              value={status}
              style={{ minWidth: 180 }}
              options={STATUSES.map((item) => ({ value: item, label: item }))}
              onChange={(value) => setFilter('status', value)}
            />
            <EducationSessionSelect
              allowClear
              placeholder={t('sourceSession')}
              value={sourceSessionId}
              style={{ minWidth: 200 }}
              onChange={(value) => setFilter('source_session_id', value || undefined)}
            />
            <EducationSessionSelect
              allowClear
              placeholder={t('targetSession')}
              value={targetSessionId}
              style={{ minWidth: 200 }}
              onChange={(value) => setFilter('target_session_id', value || undefined)}
            />
          </Space>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={rows}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/progression/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('progressionEmpty', {
                    defaultValue: 'No progression batches yet. Create a batch to promote students to the next session.',
                  })}
                />
              ),
            }}
            columns={[
              {
                title: t('sourceSession'),
                render: (_: unknown, row: BatchRow) => sessionLabel(row.source_session_code, row.source_session_name),
              },
              {
                title: t('targetSession'),
                render: (_: unknown, row: BatchRow) => sessionLabel(row.target_session_code, row.target_session_name),
              },
              {
                title: t('students', { defaultValue: 'Students' }),
                dataIndex: 'item_count',
                width: 100,
                align: 'right' as const,
              },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 150,
                render: (value: string) => <Tag color={statusColor(value)}>{value}</Tag>,
              },
              {
                title: t('schoolDecision'),
                dataIndex: 'school_decision_reason',
                ellipsis: true,
              },
              {
                title: t('createdAt', { defaultValue: 'Created' }),
                dataIndex: 'created_at',
                width: 120,
                render: (value?: string | null) => (value ? String(value).slice(0, 10) : '—'),
              },
            ]}
          />
        </Space>
      </Card>
    </EducationPageChrome>
  );
}
